import { hasAnyTranslation, translateBackend } from '@assets/i18n/config';
import { Box } from '@ovComponents/1-primative/box/box';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { useTypographyTokens } from '@ovComponents/1-primative/typography/typography.tokens';
import { Card, CardContent } from '@ovComponents/2-component/card/card';
import { useThemeTokens } from '@providers/themeTokenProvider';

export const FreeformInput = ({ options = {} }: { options?: any }) => {
  const { comp } = useThemeTokens(useTypographyTokens('regular'));
  const disableQuill = options?.disableQuill ?? false;

  const htmlRenderer = (component: JSX.Element) => {
    switch (options.showInCard) {
      case true:
        return (
          <Card variant='elevated'>
            <CardContent
              sx={{
                overflow: 'auto',
                '&:last-child': { paddingBottom: '16px' },
              }}
            >
              {component}
            </CardContent>
          </Card>
        );
      default:
        return <Box overflow={'auto'}>{component}</Box>;
    }
  };

  // Get the template content directly from options without fetching/filling
  const getTemplateContent = () => {
    if (hasAnyTranslation(options.template)) {
      const decoded = atob(translateBackend(options.template));
      return decoded;
    }
    return '';
  };

  return (
    <Box className='ql-snow'>
      <style>
        {`
          .ql-editor {
            font-family: ${comp.typography.bodyMedium.fontFamily};
          }

          .ql-editor p, .ql-editor td  {
            font-size: ${comp.typography.bodyMedium.fontSize};
            line-height: ${comp.typography.bodyMedium.lineHeight};
            font-weight: ${comp.typography.bodyMedium.fontWeight};
            letter-spacing: ${comp.typography.bodyMedium.letterSpacing};
          }
          .ql-editor p .ql-size-small, .ql-editor td .ql-size-small {
            font-size: ${comp.typography.bodySmall.fontSize};
            line-height: ${comp.typography.bodySmall.lineHeight};
            font-weight: ${comp.typography.bodySmall.fontWeight};
            letter-spacing: ${comp.typography.bodySmall.letterSpacing};
          }
          .ql-editor p .ql-size-large, .ql-editor td .ql-size-large {
            font-size: ${comp.typography.bodyLarge.fontSize};
            line-height: ${comp.typography.bodyLarge.lineHeight};
            letter-spacing: ${comp.typography.bodyLarge.letterSpacing};
            font-weight: 500;
          }
          .ql-editor p .ql-size-huge, .ql-editor td .ql-size-huge {
            font-size: ${comp.typography.trimmedLarge.fontSize};
            line-height: ${comp.typography.trimmedLarge.lineHeight};
            letter-spacing: ${comp.typography.trimmedLarge.letterSpacing};
            font-weight: 500;
          }
          .ql-editor p strong, .ql-editor td strong {
            font-weight: 600 !important;
          }
          .ql-editor h1, .ql-editor h2, .ql-editor h3,
          .ql-editor h4, .ql-editor h5, .ql-editor h6 {
            font-weight: 600;
          }
          .ql-editor h1 strong, .ql-editor h2 strong, .ql-editor h3 strong,
          .ql-editor h4 strong, .ql-editor h5 strong, .ql-editor h6 strong {
            font-weight: 700 !important;
          }
        `}
      </style>

      {translateBackend(options.customTitle) !== '' && (
        <Box display={'flex'} pb={2}>
          <Typography variant='headingSmall'>
            {translateBackend(options.customTitle)}
          </Typography>
        </Box>
      )}

      {htmlRenderer(
        <Typography
          className={disableQuill ? undefined : 'ql-editor'}
          variant='none'
          dangerouslySetInnerHTML={{
            __html: getTemplateContent(),
          }}
        />,
      )}
    </Box>
  );
};
